import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from '../context/TranslationContext';
import { useAuth } from '../context/AuthContext';
import { useQuotes } from '../context/QuotesContext';
import ResetConfigDropdown from './ResetConfigDropdown';
import {
  buildConfiguratorNavigationState,
  clearActiveContactForConfigurator,
} from '../lib/contactConfigurator';

const DARK_STYLES = {
  button:
    'px-2.5 py-1.5 sm:px-4 sm:py-2 border border-slate-500 hover:bg-slate-700 text-white rounded-lg text-xs sm:text-sm font-medium transition-colors touch-manipulation whitespace-nowrap',
  export:
    'px-2.5 py-1.5 sm:px-4 sm:py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-xs sm:text-sm font-medium transition-colors touch-manipulation whitespace-nowrap',
  quotes:
    'hidden sm:inline-flex px-3 py-1.5 sm:py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs sm:text-sm font-medium transition-colors touch-manipulation whitespace-nowrap',
};

const LIGHT_STYLES = {
  button:
    'px-2.5 py-1.5 sm:px-3 sm:py-2 border border-gray-300 hover:bg-gray-50 text-gray-800 rounded-lg text-xs sm:text-sm font-medium transition-colors touch-manipulation whitespace-nowrap',
  export:
    'px-2.5 py-1.5 sm:px-3 sm:py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-xs sm:text-sm font-medium transition-colors touch-manipulation whitespace-nowrap',
  quotes:
    'inline-flex px-2.5 py-1.5 sm:px-3 sm:py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs sm:text-sm font-medium transition-colors touch-manipulation whitespace-nowrap',
};

/**
 * Shared action cluster: Nieuwe offerte / Offerte Exporteren / Reset / My Quotes.
 * Used in ConfiguratorShell, SavedQuotes, and admin Dashboard.
 */
const ConfiguratorNavActions = ({
  onReset,
  showExport = true,
  showReset = true,
  showMyQuotes = true,
  showNewOffer = true,
  /** 'dark' = configurator/export headers; 'light' = admin dashboard. */
  variant = 'dark',
  /** Optional guard before navigating away (e.g. dirty-leave confirm). */
  attemptNavigation,
  className = '',
  buttonClassName,
  exportButtonClassName,
  quotesButtonClassName,
  onMarkClean,
}) => {
  const styles = variant === 'light' ? LIGHT_STYLES : DARK_STYLES;
  const newOfferClass = buttonClassName ?? styles.button;
  const exportClass = exportButtonClassName ?? styles.export;
  const quotesClass = quotesButtonClassName ?? styles.quotes;
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { isAuthenticated, user } = useAuth();
  const { startNewQuoteSession } = useQuotes();

  const runNav = (navFn) => {
    if (attemptNavigation) {
      attemptNavigation(navFn);
      return;
    }
    navFn();
  };

  const handleNewOffers = () => {
    runNav(() => {
      if (window.confirm(t('configurator.newOffersConfirm'))) {
        startNewQuoteSession();
        clearActiveContactForConfigurator();
        onMarkClean?.();
        navigate('/configurator-select', {
          replace: true,
          state: { newQuote: true },
        });
      }
    });
  };

  const handleExport = () => {
    runNav(() =>
      navigate('/export-quote', {
        state: buildConfiguratorNavigationState(
          location.state,
          { from: location.pathname, preserveQuoteSession: true },
          user?.id
        ),
      })
    );
  };

  const handleMyQuotes = () => {
    runNav(() => navigate('/dashboard?tab=quotes'));
  };

  return (
    <div className={`flex items-center gap-1.5 sm:gap-2 xl:gap-3 shrink-0 flex-wrap justify-end ${className}`}>
      {showNewOffer ? (
        <button type="button" onClick={handleNewOffers} className={newOfferClass}>
          {t('configurator.newOffers')}
        </button>
      ) : null}
      {showExport ? (
        <button type="button" onClick={handleExport} className={exportClass}>
          {t('configurator.exportQuote')}
        </button>
      ) : null}
      {showReset && onReset ? <ResetConfigDropdown onReset={onReset} /> : null}
      {showMyQuotes && isAuthenticated ? (
        <button type="button" onClick={handleMyQuotes} className={quotesClass}>
          {t('configurator.myQuotes')}
        </button>
      ) : null}
    </div>
  );
};

export default ConfiguratorNavActions;
